import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ContentType, TestSource } from "@/types/ab-tests";
import { SOURCE_OPTIONS, SCOPE_OPTIONS } from "./constants";
import { getTestSourceLabel, scopeLabel } from "./utils";

interface ConfigurationSectionProps {
  startedAt: string;
  stoppedAt: string;
  source: TestSource | "";
  sourceDetail: string;
  targetKeyword: string;
  scopeSet: Set<ContentType>;
  hypothesis: string;
  variantATitle: string;
  variantBTitle: string;
  variantABullets: string[];
  variantBBullets: string[];
  testedFields: ContentType[];
  onStartedAtChange: (v: string) => void;
  onStoppedAtChange: (v: string) => void;
  onSourceChange: (v: TestSource) => void;
  onSourceDetailChange: (v: string) => void;
  onTargetKeywordChange: (v: string) => void;
  onToggleScope: (f: ContentType) => void;
  onHypothesisChange: (v: string) => void;
  onVariantATitleChange: (v: string) => void;
  onVariantBTitleChange: (v: string) => void;
  onVariantABulletChange: (idx: number, v: string) => void;
  onVariantBBulletChange: (idx: number, v: string) => void;
}

export function ConfigurationSection({
  startedAt,
  stoppedAt,
  source,
  sourceDetail,
  targetKeyword,
  scopeSet,
  hypothesis,
  variantATitle,
  variantBTitle,
  variantABullets,
  variantBBullets,
  testedFields,
  onStartedAtChange,
  onStoppedAtChange,
  onSourceChange,
  onSourceDetailChange,
  onTargetKeywordChange,
  onToggleScope,
  onHypothesisChange,
  onVariantATitleChange,
  onVariantBTitleChange,
  onVariantABulletChange,
  onVariantBBulletChange,
}: ConfigurationSectionProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="started-at" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Test started</Label>
          <Input id="started-at" type="date" value={startedAt} onChange={(e) => onStartedAtChange(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stopped-at" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Test stopped</Label>
          <Input id="stopped-at" type="date" value={stoppedAt} onChange={(e) => onStoppedAtChange(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Source</Label>
          <Select value={source || undefined} onValueChange={(v) => onSourceChange(v as TestSource)}>
            <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{getTestSourceLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="target-keyword" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Target keyword</Label>
          <Input id="target-keyword" value={targetKeyword} onChange={(e) => onTargetKeywordChange(e.target.value)} placeholder="e.g., mineral sunscreen for oily skin" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="source-detail" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Source detail</Label>
        {(() => {
          let parsed: string[] | null = null;
          try {
            const result = JSON.parse(sourceDetail);
            if (Array.isArray(result) && result.every((item: unknown) => typeof item === "string")) {
              parsed = result;
            }
          } catch (_) { void _; }
          if (parsed) {
            return (
              <div className="space-y-2">
                <ul className="space-y-1 list-disc list-inside text-sm text-foreground">
                  {parsed.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">Content gaps are managed from the test creation flow.</p>
              </div>
            );
          }
          return (
            <Textarea id="source-detail" value={sourceDetail} onChange={(e) => onSourceDetailChange(e.target.value)} rows={2} placeholder="Short description of the specific change tested" />
          );
        })()}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Scope</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
          {SCOPE_OPTIONS.map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={scopeSet.has(f)} onCheckedChange={() => onToggleScope(f)} />
              <span>{scopeLabel(f)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="hypothesis" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Hypothesis</Label>
        <Textarea id="hypothesis" value={hypothesis} onChange={(e) => onHypothesisChange(e.target.value)} rows={3} />
      </div>

      {testedFields.includes("title") && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Variant A — Title</Label>
            <Textarea value={variantATitle} onChange={(e) => onVariantATitleChange(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Variant B — Title</Label>
            <Textarea value={variantBTitle} onChange={(e) => onVariantBTitleChange(e.target.value)} rows={3} />
          </div>
        </div>
      )}

      {testedFields
        .filter((f) => f.startsWith("bullet-"))
        .map((f) => {
          const idx = parseInt(f.split("-")[1], 10) - 1;
          return (
            <div key={f} className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Variant A — Bullet {idx + 1}</Label>
                <Textarea value={variantABullets[idx] || ""} onChange={(e) => onVariantABulletChange(idx, e.target.value)} rows={3} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Variant B — Bullet {idx + 1}</Label>
                <Textarea value={variantBBullets[idx] || ""} onChange={(e) => onVariantBBulletChange(idx, e.target.value)} rows={3} />
              </div>
            </div>
          );
        })}
    </div>
  );
}
